import {
  AppealCategoryData,
  BrandDetail,
  ExtVettingRecord,
  VettingAppeal,
  VettingProvider,
} from './types';
import { FeatureType, VettingType } from '../CSP-Form/constants';
import {
  AppealCategory,
  AppealSetting,
  CmsRecord,
  VettingSetting,
} from '../CSP-Form/types';
import { EntityTypes } from '../../../shared_elements/enums';

export const isValidForEnhancedVet = (entityType: string) =>
  entityType !== 'SOLE_PROPRIETOR';

export const isValidForPoliticalVet = (
  entityType: string,
  einIssuingCountry: string
) =>
  (entityType === 'SOLE_PROPRIETOR' || entityType === 'NON_PROFIT') &&
  einIssuingCountry === 'US';

export const isValidForWMC = (entityType: string, einIssuingCountry: string) =>
  (entityType === 'PRIVATE_PROFIT' || entityType === 'PUBLIC_PROFIT') &&
  einIssuingCountry === 'US';

export const isValidForAppeal = (record: ExtVettingRecord) =>
  (record.evpUid === 'AEGIS' || record.evpUid === 'WMC') &&
  record.vettingStatus.toLowerCase() !== 'expired';

export const isValidForOperation = (role: string) =>
  ['ROLE_MANAGER', 'ROLE_USER'].includes(role);

export const isValidForIdentityStatusChange = (brand: BrandDetail) =>
  brand.entityType !== EntityTypes.PubliclyTradedCompany ||
  (!!brand.website && !!brand.businessContactEmail);

export const capitalizeFirstLetter = (str: string): string => {
  return str.slice(0, 1).toUpperCase() + str.slice(1).toLowerCase();
};

export const findCmsRecordByKey = (records: CmsRecord[], key: string) =>
  records.find((record) => record.lookup_key === key);

export const filterVettingAppealsByVettingClass = (
  vettingAppeals: VettingAppeal[],
  vettingClass: string
) => vettingAppeals.filter((appeal) => appeal.vettingClass === vettingClass);

export const filterAppealCategoriesByVettingType = (
  appealCategories: AppealCategory[],
  vettingType: string
) =>
  appealCategories
    .filter((category) =>
      category.detail.some((item) => item.vettingType === vettingType)
    )
    .map((category) => {
      const item = category.detail.find(
        (item) => item.vettingType === vettingType
      );
      return {
        appealCategoryId: category.appealCategoryId,
        displayName: category.displayName,
        description: item ? item.description : '',
      } as AppealCategoryData;
    });

export const groupAppealCategoryIds = (
  vettingSettings: VettingSetting[],
  brand?: BrandDetail
) => {
  const enabledIdentityAppealCategoryIds: string[] = [];
  const disabledIdentityAppealCategoryIds: string[] = [];
  const enabledStandardAppealCategoryIds: string[] = [];
  const disabledStandardAppealCategoryIds: string[] = [];
  const enabledAssetAppealCategoryIds: string[] = [];
  const disabledAssetAppealCategoryIds: string[] = [];
  if (brand) {
    const vettingSetting = vettingSettings.find(
      (item) => item.id === brand.entityType
    );
    if (vettingSetting) {
      const feature = vettingSetting.feature.find(
        (item) => item.id === FeatureType.Appeal
      ) as AppealSetting;
      if (feature) {
        feature.evp.forEach((evp) => {
          evp.vettingType.forEach((vettingType) => {
            if (vettingType.id === VettingType.Identity) {
              vettingType.category.forEach((category) => {
                enabledIdentityAppealCategoryIds.push(category.id);
                if (
                  !category.allowAppealByIdentityStatus.includes(
                    brand.identityStatus
                  )
                ) {
                  disabledIdentityAppealCategoryIds.push(category.id);
                }
              });
            } else if (vettingType.id === VettingType.Standard) {
              vettingType.category.forEach((category) => {
                enabledStandardAppealCategoryIds.push(category.id);
                if (
                  !category.allowAppealByIdentityStatus.includes(
                    brand.identityStatus
                  )
                ) {
                  disabledStandardAppealCategoryIds.push(category.id);
                }
              });
            } else if (vettingType.id === VettingType.Asset) {
              vettingType.category.forEach((category) => {
                if (
                  category.allowAppealByIdentityStatus.includes(
                    brand.identityStatus
                  )
                ) {
                  enabledAssetAppealCategoryIds.push(category.id);
                } else {
                  disabledAssetAppealCategoryIds.push(category.id);
                }
              });
            }
          });
        });
      }
    }
  }
  return {
    enabledIdentityAppealCategoryIds,
    disabledIdentityAppealCategoryIds,
    enabledStandardAppealCategoryIds,
    disabledStandardAppealCategoryIds,
    enabledAssetAppealCategoryIds,
    disabledAssetAppealCategoryIds,
  };
};

export const filterVettingClassOptionsByBrand = (
  vettingProvidersByVettingClasses: Record<string, VettingProvider[]>,
  brand?: BrandDetail
) => {
  if (!brand) {
    return [];
  }

  const supportNewVet = (vettingClass: string) =>
    vettingProvidersByVettingClasses[vettingClass].some(
      (item) => !!item.vettingInstruction.newVet?.supported
    );
  const enhancedVetSupported = isValidForEnhancedVet(brand.entityType);
  const politicalVetSupported = isValidForPoliticalVet(
    brand.entityType,
    brand.einIssuingCountry
  );
  return Object.keys(vettingProvidersByVettingClasses)
    .filter(
      (vettingClass) => vettingClass !== 'ENHANCED' || enhancedVetSupported
    )
    .filter(
      (vettingClass) => vettingClass !== 'POLITICAL' || politicalVetSupported
    )
    .filter(supportNewVet)
    .map((vettingClass) => ({ label: vettingClass, value: vettingClass }));
};

export const filterVettingProviderOptionsByBrand = (
  vettingProviders: VettingProvider[],
  newVet: boolean,
  brand?: BrandDetail
) => {
  if (!brand) {
    return [];
  }

  const wmcSupported = isValidForWMC(brand.entityType, brand.einIssuingCountry);
  return vettingProviders
    .filter((vettingProvider) => vettingProvider.uid !== 'WMC' || wmcSupported)
    .filter(
      (vettingProvider) =>
        !!vettingProvider.vettingInstruction[newVet ? 'newVet' : 'importVet']
          ?.supported
    );
};

export const estimatedTimeByVettingClass = (
  vettingClass: string,
  extVettingProviderId: string,
  vettingProvider?: VettingProvider
) => {
  if (extVettingProviderId === 'AEGIS') {
    if (vettingClass === 'STANDARD') {
      return 'Up to 3 business days. Time varies based on vetting provider capacity.';
    }
    if (vettingClass === 'ENHANCED') {
      return 'Up to 5 business days. Time varies based on vetting provider capacity.';
    }
    if (vettingClass === 'POLITICAL') {
      return 'Under normal circumstances, you can expect your PIN code to be sent within one business day of application. Final vetting will occur within an hour of your PIN confirmation action.';
    }
    if (vettingClass === 'AUTHPLUS') {
      return 'Time varies based on the vetting provider’s capacity and how quickly the brand’s business contact completes the email verification.';
    }
  }

  return vettingProvider?.vettingInstruction.newVet?.waitTime || '--';
};

export const isBusinessContactEmailRequired = (entityType?: string) => {
  return entityType === EntityTypes.PubliclyTradedCompany;
};

export const isBusinessContactEmailVisible = (entityType?: string) => {
  return entityType !== EntityTypes.SoleProprietor;
};

export const getAppealCategoryIdGroups = (
  vettingSettings: VettingSetting[],
  brand: BrandDetail,
  vettingTypeId: VettingType
) => {
  const validAppealCategoryIds: string[] = [];
  const disabledAppealCategoryIds: string[] = [];
  if (brand) {
    const vettingSetting = vettingSettings.find(
      (item) => item.id === brand.entityType
    );
    if (vettingSetting) {
      const feature = vettingSetting.feature.find(
        (item) => item.id === FeatureType.Appeal
      ) as AppealSetting;
      if (feature) {
        feature.evp.forEach((evp) => {
          evp.vettingType.forEach((vettingType) => {
            if (vettingType.id === vettingTypeId) {
              vettingType.category.forEach((category) => {
                validAppealCategoryIds.push(category.id);
                if (
                  !category.allowAppealByIdentityStatus.includes(
                    brand.identityStatus
                  )
                ) {
                  disabledAppealCategoryIds.push(category.id);
                }
              });
            }
          });
        });
      }
    }
  }
  return {
    validAppealCategoryIds,
    disabledAppealCategoryIds,
  };
};

export const getRussell3000 = (brand: {
  optionalAttributes?: BrandDetail['optionalAttributes'];
}) => {
  return brand?.optionalAttributes?.russell3000 ? 'True' : 'False';
};

export const getPoliticalCommitteeLocale = (brand: {
  optionalAttributes?: BrandDetail['optionalAttributes'];
}) => {
  const politicalCommitteeLocale =
    brand?.optionalAttributes?.politicalCommitteeLocale;
  return politicalCommitteeLocale
    ? `${politicalCommitteeLocale[0].toUpperCase()}${politicalCommitteeLocale.slice(
        1
      )}`
    : null;
};

export const getGovernmentEntity = (brand: {
  optionalAttributes?: BrandDetail['optionalAttributes'];
}) => {
  return brand?.optionalAttributes?.governmentEntity ? 'True' : 'False';
};

export const getTaxExemptStatus = (brand: {
  optionalAttributes?: BrandDetail['optionalAttributes'];
}) => {
  const optionalAttributes = brand?.optionalAttributes;
  if (optionalAttributes?.section527) {
    return '527';
  }
  if (optionalAttributes?.taxExemptStatus) {
    return optionalAttributes.taxExemptStatus;
  }
};

export const getBadgeTypeByStatus = (status: string | null) => {
  switch (status?.toLowerCase()) {
    case 'registered':
    case 'verified':
    case 'vetted_verified':
    case 'completed':
    case 'active':
      return 'success';
    case 'unverified':
    case 'not_submitted':
    case 'not_sent':
    case 'review':
      return 'warning';
    case 'expired':
    case 'ended':
    case 'voided':
    case 'declined':
      return 'error';
    case 'pending':
    case 'sent_for_signature':
    case 'sent':
      return 'info';
    default:
      return 'neutral';
  }
};
